import PropTypes from "prop-types";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Button, Link, Modal } from "@yoast/ui-library";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import ProvisionedDate from "./ProvisionedDate";
import ProvisionedSubscriptionStatusBadge from "./ProvisionedSubscriptionStatusBadge";
import * as modalStyles from "./ProvisioningStyles.module.scss";
import * as styles from "./CreateSubscription.module.scss";

const messages = defineMessages( {
	title: {
		id: "provisioning.result.title",
		defaultMessage: "Subscription created",
	},
	ariaLabel: {
		id: "provisioning.result.ariaLabel",
		defaultMessage: "The created subscription",
	},
	number: {
		id: "provisioning.result.number",
		defaultMessage: "Number",
	},
	status: {
		id: "provisioning.result.status",
		defaultMessage: "Status",
	},
	startDate: {
		id: "provisioning.result.startDate",
		defaultMessage: "Start date",
	},
	endDate: {
		id: "provisioning.result.endDate",
		defaultMessage: "End date",
	},
	site: {
		id: "provisioning.result.site",
		defaultMessage: "Site",
	},
	downloads: {
		id: "provisioning.result.downloads",
		defaultMessage: "Plugin downloads",
	},
	noDownloads: {
		id: "provisioning.result.noDownloads",
		defaultMessage: "No downloads available.",
	},
	noSite: {
		id: "provisioning.result.noSite",
		defaultMessage: "No site",
	},
	view: {
		id: "provisioning.result.view",
		defaultMessage: "View subscription",
	},
	done: {
		id: "provisioning.result.done",
		defaultMessage: "Done",
	},
} );

/**
 * Shows the subscription that was just provisioned, with its plugin download links.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element} The modal.
 */
function ProvisionedSubscriptionResultModal( { subscription = null, onClose, onView = null, intl } ) {
	return (
		<Modal
			isOpen={ Boolean( subscription ) }
			onClose={ onClose }
			aria-label={ intl.formatMessage( messages.ariaLabel ) }
		>
			<Modal.Panel>
				<Modal.Title as="h2" size="3">
					<FormattedMessage { ...messages.title } />
				</Modal.Title>
				{ subscription && (
					<div className={ modalStyles.modalBody }>
						<dl className={ styles.resultDetails }>
							<dt><FormattedMessage { ...messages.number } /></dt>
							<dd>{ subscription.subscriptionNumber }</dd>
							<dt><FormattedMessage { ...messages.status } /></dt>
							<dd><ProvisionedSubscriptionStatusBadge status={ subscription.status } /></dd>
							<dt><FormattedMessage { ...messages.startDate } /></dt>
							<dd><ProvisionedDate value={ subscription.startDate } /></dd>
							<dt><FormattedMessage { ...messages.endDate } /></dt>
							<dd><ProvisionedDate value={ subscription.endDate } /></dd>
							<dt><FormattedMessage { ...messages.site } /></dt>
							<dd>{ subscription.siteUrl || <FormattedMessage { ...messages.noSite } /> }</dd>
							<dt><FormattedMessage { ...messages.downloads } /></dt>
							<dd>
								{ subscription.pluginDownloadUrls.length === 0
									? <FormattedMessage { ...messages.noDownloads } />
									: (
										<ul className={ styles.downloadList }>
											{ subscription.pluginDownloadUrls.map( url => (
												<li key={ url }>
													<Link href={ url } target="_blank" rel="noopener noreferrer">{ url }</Link>
												</li>
											) ) }
										</ul>
									)
								}
							</dd>
						</dl>
					</div>
				) }
				<div className={ modalStyles.modalActions }>
					{ subscription && (
						<Button
							as={ RouterLink }
							variant="secondary"
							to={ `/provisioning/subscriptions/${ subscription.id }` }
							onClick={ onView || onClose }
						>
							<FormattedMessage { ...messages.view } />
						</Button>
					) }
					<Button variant="primary" onClick={ onClose }>
						<FormattedMessage { ...messages.done } />
					</Button>
				</div>
			</Modal.Panel>
		</Modal>
	);
}

ProvisionedSubscriptionResultModal.propTypes = {
	subscription: PropTypes.shape( {
		id: PropTypes.string.isRequired,
		subscriptionNumber: PropTypes.string,
		status: PropTypes.string,
		startDate: PropTypes.instanceOf( Date ),
		endDate: PropTypes.instanceOf( Date ),
		siteUrl: PropTypes.string,
		pluginDownloadUrls: PropTypes.arrayOf( PropTypes.string ).isRequired,
	} ),
	onClose: PropTypes.func.isRequired,
	onView: PropTypes.func,
	intl: intlShape.isRequired,
};

export default injectIntl( ProvisionedSubscriptionResultModal );
